<template>
  <div>
    <page-header>
      <template #title>
        <h1 class="page-title">
          FNOL
          <small>(First Notification of Loss)</small>
        </h1>
      </template>
      <template #actions>
        <page-header-action
          tag="form-button"
          :indicator="referringToAgent"
          @clicked="referToAgentCheck"
          v-if="hasAgent"
        >
          Refer Case to Agent
        </page-header-action>
        <page-header-action
          tag="form-button"
          :indicator="proceeding"
          @clicked="newClaimsAdvice"
          v-if="hasModule('new_claims_advice')"
        >
          Proceed To New Claims Advice
        </page-header-action>
        <page-header-action @click.prevent="goBack">
          Back To Incident Details
        </page-header-action>
      </template>
    </page-header>
    <div class="row">
      <div
        :class="{
          'col-lg-4': hasStorageAndRecovery,
          'col-lg-6': !hasStorageAndRecovery
        }"
      >
        <div class="card mb-3">
          <div class="card-heading">
            Vehicle Hire Agreement
          </div>
          <div class="card-preview text-center">
            <transition name="component-fade">
              <div class="processing-overlay" v-if="refresh">
                Regenerating Document...
              </div>
            </transition>
            <div class="approved-overlay"></div>
            <img
              class="img-fluid"
              alt="Vehicle Hire Agreement"
              :src="vehicleHireAgreementThumb"
            />
          </div>
          <div class="card-controls">
            <a
              target="_blank"
              class="btn btn-block btn-dark btn-large-padding"
              :href="vehicleHireAgreementPath"
              >View Document</a
            >
            <a
              target="_blank"
              v-on:click.prevent="regenerateDocument"
              class="btn btn-block btn-primary no-margin btn-large-padding"
              >Regenerate Document</a
            >
          </div>
        </div>
      </div>
      <div
        :class="{
          'col-lg-4': hasStorageAndRecovery,
          'col-lg-6': !hasStorageAndRecovery
        }"
      >
        <div class="card mb-3">
          <div class="card-heading">
            Vehicle Hire Details
          </div>
        </div>
        <div class="card">
          <div class="card-body">
            <table class="table-bordered table-striped table" v-if="hire">
              <tbody>
                <tr>
                  <th width="50%">Registration</th>
                  <td>{{ hire.vehicle.registration }}</td>
                </tr>
                <tr>
                  <th width="50%">Start Date</th>
                  <td v-html="hireStartDate"></td>
                </tr>
                <tr>
                  <th width="50%">Daily Rate</th>
                  <td v-html="dailyRate"></td>
                </tr>
                <tr>
                  <th width="50%">Collision Damage Waiver</th>
                  <td v-html="collisionDamageWaiver"></td>
                </tr>
                <tr>
                  <th width="50%">Automatic</th>
                  <td v-html="automatic"></td>
                </tr>
                <tr>
                  <th width="50%">
                    Additional Drivers ({{ totalAdditionalDrivers }})
                  </th>
                  <td v-html="additional"></td>
                </tr>
                <tr>
                  <th width="50%">Estate</th>
                  <td v-html="estate"></td>
                </tr>
                <tr>
                  <th width="50%">Other</th>
                  <td v-html="other"></td>
                </tr>
                <tr>
                  <th width="50%">Total Daily Rate</th>
                  <td v-html="totalDailyRate"></td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
      <div class="col-lg-4" v-if="hasStorageAndRecovery">
        <div class="card mb-3">
          <div class="card-heading">
            Storage and Recovery
          </div>
          <div class="card-preview text-center">
            <div class="approved-overlay"></div>
            <img
              class="img-fluid"
              alt="Storage and Recovery Document"
              :src="storageAndRecoveryThumb"
            />
          </div>
          <div class="card-controls">
            <a
              target="_blank"
              class="btn btn-block btn-dark btn-large-padding"
              :href="storageAndRecoveryPath"
              >View Document</a
            >
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import PageHeader from './../Shared/PageHeader.vue';
import PageHeaderAction from './../Shared/PageHeaderAction.vue';

export default {
  components: {
    PageHeader,
    PageHeaderAction
  },

  props: {
    incident: {
      type: String,
      required: true
    }
  },

  data() {
    return {
      proceeding: false,
      referringToAgent: false,
      hire: null,
      refresh: false,
      documents: []
    };
  },

  computed: {
    hasStorageAndRecovery() {
      return (
        this.documents.findIndex(document => {
          return document.type.name === 'storage_and_recovery';
        }) > -1
      );
    },

    additional() {
      if (this.hire) {
        return (
          '&pound;' +
          (this.hire.vehicle.rates.additional / 100).toFixed(2) +
          ' per driver'
        );
      }

      return '';
    },

    totalAdditionalDrivers() {
      if (this.hire) {
        return this.hire.drivers.filter(driver => {
          return driver.type_id !== 1;
        }).length;
      }

      return 0;
    },

    totalDailyRate() {
      let total =
        parseInt(
          this.hire.vehicle.rates.additional * this.totalAdditionalDrivers
        ) +
        parseInt(this.hire.vehicle.rates.other) +
        parseInt(this.hire.vehicle.rates.estate) +
        parseInt(this.hire.vehicle.rates.automatic) +
        parseInt(this.hire.vehicle.rates.collision_damage_waiver) +
        parseInt(this.hire.vehicle.rates.daily);

      return '&pound;' + (total / 100).toFixed(2);
    },

    other() {
      if (this.hire) {
        return '&pound;' + (this.hire.vehicle.rates.other / 100).toFixed(2);
      }

      return '';
    },

    estate() {
      if (this.hire) {
        return '&pound;' + (this.hire.vehicle.rates.estate / 100).toFixed(2);
      }

      return '';
    },

    automatic() {
      if (this.hire) {
        return '&pound;' + (this.hire.vehicle.rates.automatic / 100).toFixed(2);
      }

      return '';
    },

    collisionDamageWaiver() {
      if (this.hire) {
        return (
          '&pound;' +
          (this.hire.vehicle.rates.collision_damage_waiver / 100).toFixed(2)
        );
      }

      return '';
    },

    dailyRate() {
      if (this.hire) {
        return '&pound;' + (this.hire.vehicle.rates.daily / 100).toFixed(2);
      }

      return '';
    },

    hireStartDate() {
      if (this.hire) {
        return window.DateManager.format(this.hire.vehicle.start_date);
      }

      return '';
    },

    vehicleHireAgreementThumb() {
      if (!this.refresh) {
        return (
          window.AxisUK.api +
          'incidents/' +
          this.incident +
          '/documents/thumb/vehicle-hire-agreement?token=' +
          window.Token.get()
        );
      }

      return '';
    },

    vehicleHireAgreementPath() {
      let document = this.documents.find(document => {
        return document.type.name === 'vehicle_hire_agreement';
      });

      if (!this.refresh && document) {
        return `${window.AxisUK.storage}${document.hash}`;
      }

      return '';
    },

    storageAndRecoveryThumb() {
      return (
        window.AxisUK.api +
        'incidents/' +
        this.incident +
        '/documents/thumb/storage-and-recovery?token=' +
        window.Token.get()
      );
    },

    storageAndRecoveryPath() {
      let document = this.documents.find(document => {
        return document.type.name === 'storage_and_recovery';
      });

      if (!this.refresh && document) {
        return `${window.AxisUK.storage}${document.hash}`;
      }

      return '';
    },

    hasAgent() {
      return false;
      // return !! this.$store.getters.agent;
    }
  },

  mounted() {
    window.Ajax.get('/incidents/' + this.incident)
      .then(response => {
        if (response.data.credit_hire) {
          this.hire = response.data.credit_hire;
          this.getDocuments();
        } else {
          this.$router.push('/first-notice-of-loss/' + this.incident);
        }
      })
      .catch(error => {});
  },

  methods: {
    getDocuments() {
      window.Ajax.get(`/incidents/${this.incident}/documents`)
        .then(response => {
          this.documents = response.data;
        })
        .catch(error => {});
    },

    referToAgentCheck() {
      window
        .swal({
          title: 'Are you sure?',
          text: `This will instruct the agent to manage this case for you`,
          type: 'warning',
          showCancelButton: true
        })
        .then(() => {
          let agent = this.$store.getters.agent;
          this.referringToAgent = true;
          window.Ajax.post(
            `/incidents/${this.incident}/instruct-agent/${agent.identifier}`
          )
            .then(response => {
              window.Ajax.post(
                '/first-notice-of-loss/' + this.incident + '/complete'
              )
                .then(response => {
                  window.toastify.success(
                    'Success!',
                    'The agent was instructed successfully!'
                  );
                  this.$router.push('/new-claims-advice/');
                  this.referringToAgent = false;
                })
                .catch(error => {
                  this.referringToAgent = false;
                  window.toastify.error(
                    'Error',
                    "Sorry, we couldn't instruct the agent"
                  );
                });
            })
            .catch(error => {
              this.referringToAgent = false;
              window.toastify.error(
                'Error',
                "Sorry, we couldn't instruct the agent"
              );
            });
        })
        .catch(error => {});
    },

    newClaimsAdvice() {
      this.proceeding = true;
      window.Ajax.post('/first-notice-of-loss/' + this.incident + '/complete')
        .then(response => {
          this.$router.push('/new-claims-advice/' + this.incident);
          this.proceeding = false;
        })
        .catch(error => {
          this.proceeding = false;
          window.toastify.error(
            'Error',
            "Sorry, we couldn't move the incident to new claims advice"
          );
        });
    },

    regenerateDocument() {
      this.refresh = true;

      setTimeout(() => {
        window.Ajax.post('/hires/' + this.hire.identifier + '/complete')
          .then(response => {
            this.refresh = false;
            window.toastify.success(
              'Regenerated',
              'The document was regenerated successfully!'
            );
          })
          .catch(error => {
            this.refresh = false;
            window.toastify.error(
              'Error',
              'Sorry, we could not regenerate the document for you'
            );
          });
      }, 1000);
    },

    goBack() {
      this.$router.push('/first-notice-of-loss/' + this.incident);
    }
  }
};
</script>
